import sdk, { BaseSDK } from '../sdk'

import { logger } from '../utils'

import { StashApi, StashReleaseItem } from '../services/api/stash'

import {
  CatalogApi,
  LegacyReleaseResponse,
  CatalogReleaseItem,
  CatalogReleaseTypeD,
  CatalogReleaseTypeC,
} from '../services/api/catalog'

import type { ReleaseProduct } from '../types'

import { HOUR } from '../constants'

import { _config, normalizeArtist, normalizeLegacyRelease, normalizeReleaseItem, normalizeStashItem } from '../utils/data'
import { useCache } from '../utils/cache'
import { CONTENT_SUBTYPE } from '../constants/enums'
import type { AbstractStorage } from '../utils/storage'

type ReleaseOptions = {
  waitAuth?: boolean
  store?: Map<string, unknown> | AbstractStorage
}

const CACHE_LIFETIME = 1 * HOUR

const catalogApi = new CatalogApi('api')
const stashApi = new StashApi('api')

export const isNormalized = Symbol('isNormalized')

export class Release<T> implements ReleaseProduct {
  get id() {
    return this.#data.id
  }

  get uuid() {
    return this.#data.uuid
  }

  get status() {
    return this.#data.status
  }

  get name() {
    return this.#data.name || this.show.title || this.album.title
  }

  get duration() {
    return this.#data.duration
  }

  get type() {
    return this.#data.type
  }

  get album() {
    return this.#data.album
  }

  get show() {
    return this.#data.show
  }

  get addedAt() {
    return this.#data.addedAt
  }

  get isPurchased() {
    return this.#data.isPurchased
  }

  get isPreordered() {
    return this.#data.isPreordered
  }

  get isAudio() {
    return this.#data.isAudio
  }

  get isSony360() {
    return this.#data.isSony360
  }

  get isVOD() {
    return this.#data.isVOD
  }

  get isSVOD() {
    return this.#data.isSVOD
  }

  get productId() {
    return this.#data.productId
  }

  get orderId() {
    return this.#data.orderId
  }

  get format() {
    return this.#data.format
  }

  get content() {
    return this.#data.content
  }

  get isBySubscription() {
    return this.#data.isBySubscription
  }

  get expiriesAt() {
    return this.#data.expiriesAt
  }

  get stream() {
    return this.#data.stream
  }

  get tracks() {
    return [...this.#data.tracks]
  }

  get venue() {
    return this.#data.venue
  }

  get artist() {
    return this.#data.artist
  }

  get chapters() {
    return this.#data.chapters
  }

  get notes() {
    return [...this.#data.notes]
  }

  get reviews() {
    return this.#data.reviews
  }

  get cover() {
    return this.#data.cover
  }

  get poster() {
    return this.#data.poster
  }

  get data(): ReleaseProduct {
    return { ...this.#data }
  }

  #data: ReleaseProduct

  [isNormalized]: boolean

  static sdk: BaseSDK = sdk

  static api = {
    catalog: catalogApi,
    stash: stashApi
  }

  static get logger() {
    return this.sdk.logger || logger
  }

  static async fetch(id: string | number, mode: 'mixed' | 'release' | 'stash' = 'mixed', options: ReleaseOptions = { waitAuth: false, store: undefined }): Promise<Release<ReleaseProduct>> {
    if (['mixed', 'release', 'stash'].indexOf(mode) === -1) throw new Error('Invalid mode')

    const { readAndCache: readAndCacheRelease } = useCache<Nullable<LegacyReleaseResponse>>(
      `release:${+id}`,
      CACHE_LIFETIME,
      null,
      () => this.api.catalog.fetchReleaseItem(id),
      options.store as never,
      true
    )

    const { readAndCache: readAndCacheStash } = useCache<Nullable<StashReleaseItem>>(
      `stash:${+id}`,
      CACHE_LIFETIME,
      null,
      async () => {
        if (options?.waitAuth && this.sdk) {
          await this.sdk.isStateReady
          return this.sdk.account.isSubscribed && this.sdk.account.stash.hasContent ? stashApi.fetchStashItem(id) : undefined as never
        } else return this.api.stash.fetchStashItem(id)
      },
      options.store as never,
      true
    )

    const [releaseResponse, stashResponse] = mode === 'mixed' ?
      await Promise.allSettled([readAndCacheRelease(), readAndCacheStash()]) :
      await Promise.allSettled([
        mode === 'release' ? readAndCacheRelease() : Promise.resolve(null),
        mode === 'stash' ? readAndCacheStash() : Promise.resolve(null)
      ])

    const data = merge(releaseResponse, stashResponse)

    Object.defineProperty(data, isNormalized, _config)

    return new this(data)
  }

  static use(data: ReleaseProduct) {
    Object.defineProperty(data, isNormalized, { value: true, enumerable: false, writable: false })
    return new this(data)
  }

  constructor(data: T) {
    this.#data = normalize<never, ReleaseProduct>(data as never)

    this[isNormalized] = true
  }
}

function merge(a: PromiseSettledResult<Nullable<LegacyReleaseResponse>>, b: PromiseSettledResult<Nullable<StashReleaseItem>>) {
  const release = a && a.status === 'fulfilled' && a.value ? normalizeLegacyRelease(a.value) : { id: null } as unknown as ReleaseProduct
  const stash = b && b.status === 'fulfilled' && b.value ? normalizeStashItem(b.value) : { id: null } as unknown as ReleaseProduct

  return release && release.id ? {
    ...stash,
    ...release,
    isPurchased: stash && stash.isPurchased,
  } : { ...release, ...stash } as ReleaseProduct
}

function has<T>(data: T, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(data, key)
}

export function normalize<T extends LegacyReleaseResponse | CatalogReleaseItem | StashReleaseItem | ReleaseProduct | Record<string, unknown> | object, K>(data: T): K {
  if (typeof data === 'object' && (Object.prototype.hasOwnProperty.call(data, isNormalized) || data instanceof Release)) {
    return data as unknown as K
  } else if (typeof data === 'object' && data !== null && Object.keys(data).length > 0) {
    const isLegacyRelease = has(data, 'containerID') && has(data, 'containerCode') && has(data, 'artistID')
    const isStashItem = has(data, 'orderId') && has(data, 'id') && has(data, 'product') && has(data, 'accessExpiresAt')
    const hasRelease = has(data, 'release')
    const hasData = has(data, 'data') && has(data, 'type') && (<CatalogReleaseTypeD>data).type === 'release'
    const hasArtist = has(data, 'type') && has(data, 'data') && (<CatalogReleaseTypeD>data).type === 'artist' || (has(data, 'avatarImage') && has(data, 'abbreviation'))

    switch (true) {
      case hasArtist:
        return normalizeArtist((<CatalogReleaseTypeD>data)) as unknown as K
      case hasData:
        return normalizeReleaseItem((<CatalogReleaseTypeD>data).data as CatalogReleaseItem, { priority: 0, type: 'show' }) as unknown as K
      case isLegacyRelease:
        return normalizeLegacyRelease(data as LegacyReleaseResponse) as unknown as K
      case isStashItem:
        return normalizeStashItem(data as StashReleaseItem) as unknown as K
      case hasRelease:
        return normalizeReleaseItem((<CatalogReleaseTypeC>data).release as CatalogReleaseItem, {
          name: data['title' as never],
          duration: data['duration' as never],
          productId: data['id' as never]
        }) as unknown as K
      default:
        return normalizeReleaseItem(data as CatalogReleaseItem, { priority: data['priority' as never] || 0 }) as unknown as K
    }
  }
  const state = {}

  Object.defineProperty(state, isNormalized, _config)

  return state as unknown as K
}

export default (): ReleaseProduct => ({
  id: '',
  uuid: '',
  name: '',
  status: 'available',
  type: CONTENT_SUBTYPE.SHOW,
  productId: '',
  orderId: undefined,
  format: undefined,
  isAudio: false,
  isPreordered: false,
  tracks: [],
  venue: {
    id: null,
    name: '',
    city: '',
    state: '',
    date: null,
    title: ''
  },
  artist: {
    key: '',
    id: '',
    name: '',
    cover: {
      url: null
    },
    avatar: {
      url: null
    }
  },
  notes: [],
  show: {
    id: '',
    title: '',
    nextId: null,
    prevId: null,
    performancedAt: '',
    count: 0
  },
  album: {
    id: null,
    title: '',
    releasedAt: '',
    count: 0
  },
  duration: 0,
  isBySubscription: false,
  isPurchased: false,
  stream: {
    status: '-',
    type: [],
    isLive: false,
    isFree: false,
    isHD: false,
    isLossless: false,
    isExclusive: false,
    isPreordered: false,
    isFreeAudio: false,
    isRecent: false,
    startsAt: null,
    startsIn: NaN,
    endsAt: null,
    timezone: null,
  },
  isSony360: false,
  isVOD: false,
  isSVOD: false,
  expiriesAt: null,
  addedAt: '',
  reviews: {
    items: [],
    total: 0
  },
  cover: {
    aspectRatio: 1,
    url: null
  },
  poster: {
    aspectRatio: 1,
    url: null
  },
  chapters: []
})

export { ReleaseProduct }
